/* Share section — image-default presentation. The right column is a
   square ShareGrid image (rendered via shareImage.ts onto the
   PalinDragon monogram backdrop); the left column hosts the action row
   (Share…, Copy Image, Copy Text). The image regenerates automatically
   as guesses are committed and reveals are purchased.

   The text-grid presentation is gone from the visible UI, but the same
   emoji-string is still produced for the Copy → Text path (clipboard
   muscle memory) and the Web-Share fallback when files aren't
   shareable on the platform. */

import { useEffect, useState } from "preact/hooks";
import {
  guesses,
  HINT_COST,
  hintPurchasedAt,
  markShareCopied,
  marksByGuess,
  puzzle,
  puzzleNumber,
  SPACES_COST,
  spacesPurchased,
  spacesPurchasedAt,
  status,
} from "../game/state";
import { buildShareGrid, buildShareText } from "../game/share";
import { renderShareImageBlob } from "../game/shareImage";

const SITE_URL = "https://palindragon.com/";

const SHARE_ICON_PATH =
  "M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z";

// Material Design "content_copy" — two overlapping rectangles, the
// universal "copy to clipboard" affordance.
const CLIPBOARD_ICON_PATH =
  "M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z";

type CopyState = "idle" | "text" | "image";

function ActionIcon({ d }: { d: string }) {
  return (
    <svg
      class="sharegrid__icon-svg"
      viewBox="0 0 24 24"
      width="22"
      height="22"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d={d} />
    </svg>
  );
}

export function ShareGrid() {
  const allMarks = marksByGuess.value;
  const p = puzzle.value;
  // Don't render the share section until a puzzle is loaded — its
  // dimensions and emoji rows are computed off the solution.
  if (p == null) return null;
  // When the player hasn't bought Spaces, render the share grid in the
  // same bare-tile shape the board shows: letters-only, no spacing or
  // punctuation. `solutionNormalized` is letters-only lowercase; case
  // doesn't matter (every letter renders to an emoji), so it's a clean
  // proxy for "the bare mask".
  const renderText = spacesPurchased.value ? p.text : p.solutionNormalized;
  const text = buildShareText(
    allMarks,
    puzzleNumber.value,
    renderText,
    p.maxGuesses,
    hintPurchasedAt.value,
    HINT_COST,
    spacesPurchasedAt.value,
    SPACES_COST,
    status.value === "won",
  );
  const [copied, setCopied] = useState<CopyState>("idle");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  // Detect Web Share API after mount to avoid SSR/hydration drift and
  // a flash of a non-functional button if the component ever renders
  // server-side.
  const [canShare, setCanShare] = useState(false);
  useEffect(() => {
    setCanShare(
      typeof navigator !== "undefined" && typeof navigator.share === "function",
    );
  }, []);

  // Regenerate the displayed image whenever any input that drives the
  // grid changes. Each new blob is exposed via URL.createObjectURL; the
  // sibling effect (below) revokes the prior URL when state advances or
  // the component unmounts, so we don't leak object URLs over a session.
  useEffect(() => {
    let cancelled = false;
    const grid = buildShareGrid(
      allMarks,
      puzzleNumber.value,
      renderText,
      p.maxGuesses,
      hintPurchasedAt.value,
      HINT_COST,
      spacesPurchasedAt.value,
      SPACES_COST,
      status.value === "won",
    );
    renderShareImageBlob(grid)
      .then((blob) => {
        if (cancelled) return;
        setImageUrl(URL.createObjectURL(blob));
      })
      .catch(() => {
        /* canvas.toBlob is the only failure path and is rare in
           practice — leaving the previous image in place is the right
           default. */
      });
    return () => {
      cancelled = true;
    };
  }, [
    allMarks,
    puzzleNumber.value,
    renderText,
    p.maxGuesses,
    hintPurchasedAt.value,
    spacesPurchasedAt.value,
    status.value,
  ]);

  useEffect(() => {
    if (imageUrl == null) return;
    return () => URL.revokeObjectURL(imageUrl);
  }, [imageUrl]);

  // Compose the clipboard-text payload (text + dev-mode appends + the
  // canonical site URL). Centralized so both Copy paths stay in sync —
  // the image variant uses the same "what did the player share" data
  // model as the text variant; the only differences are the format and
  // the rendering target.
  const composeCopyText = (): string => {
    let copyText = text;
    let ribbonVisible = false;
    if (import.meta.env.DEV) {
      // The dev ribbon's visibility (Escape toggles it) decides whether
      // dev-only appends ride along on Copy. Hidden ribbon → dev mode is
      // out of the way and the clipboard payload matches what a player
      // would copy in production. Visible ribbon → the dev gets the
      // playing-state keyboard summary or the terminal-state solution
      // reveal, depending on game status.
      ribbonVisible =
        typeof document !== "undefined" &&
        document.body.classList.contains("body--has-dev-ribbon");
      if (ribbonVisible) {
        if (status.value === "playing") {
          const debug = (globalThis as typeof globalThis & {
            __PD_KEYBOARD_DEBUG__?: (
              guessRows: readonly (readonly (string | null)[])[],
              marks: readonly (readonly import("../game/scoring").Mark[])[],
              status: "playing" | "won" | "lost",
            ) => string | null;
          }).__PD_KEYBOARD_DEBUG__;
          if (debug != null) {
            const append = debug(
              guesses.value,
              marksByGuess.value,
              "playing",
            );
            if (append != null) copyText = `${text}\n\n${append}`;
          }
        } else {
          copyText = `${text}\n${p.text}\n`;
        }
      }
    }
    // Production and dev-ribbon-hidden: stamp the canonical site URL
    // as the last line so a shared / pasted Share Grid always carries
    // a clickable "where do I play this" pointer. With the dev ribbon
    // visible, the dev appends are the trailing context instead.
    if (!ribbonVisible) {
      copyText = `${copyText}\n${SITE_URL}`;
    }
    return copyText;
  };

  const buildShareImageFile = async (): Promise<File> => {
    const grid = buildShareGrid(
      allMarks,
      puzzleNumber.value,
      renderText,
      p.maxGuesses,
      hintPurchasedAt.value,
      HINT_COST,
      spacesPurchasedAt.value,
      SPACES_COST,
      status.value === "won",
    );
    const blob = await renderShareImageBlob(grid);
    return new File([blob], `palindragon-${puzzleNumber.value}.png`, {
      type: "image/png",
    });
  };

  const flashCopied = (which: Exclude<CopyState, "idle">) => {
    setCopied(which);
    window.setTimeout(() => setCopied("idle"), 1500);
  };

  const onCopyText = async () => {
    try {
      await navigator.clipboard.writeText(composeCopyText());
      flashCopied("text");
      // Beacon `Share Copied` is dispatched on the success branch only —
      // a thrown clipboard write (insecure context / denied permission)
      // does not count as a share, and the dedupe ledger in state.ts
      // collapses repeated taps to a single event per game.
      markShareCopied("text");
    } catch {
      /* Clipboard API may be blocked (insecure context, denied permission).
         Silently leave the button label unchanged rather than misleading. */
    }
  };

  const onCopyImage = async () => {
    try {
      // Safari requires the ClipboardItem value to be a Promise<Blob> when
      // the blob is generated asynchronously, so the user-gesture click
      // handler can return synchronously while the encode finishes.
      // Chrome/Firefox accept either a Blob or a Promise<Blob>.
      const blobPromise = buildShareImageFile();
      const item = new ClipboardItem({ "image/png": blobPromise });
      await navigator.clipboard.write([item]);
      flashCopied("image");
      markShareCopied("image");
    } catch {
      /* Clipboard image-write may be unsupported (older Safari, Firefox
         without the dom.events.asyncClipboard.clipboardItem pref) or
         blocked. The Text button remains as a fallback. */
    }
  };

  const onNativeShare = async () => {
    try {
      const file = await buildShareImageFile();
      // Probe with the real File — some platforms gate file shares by
      // MIME type, size, or origin. When files aren't shareable, fall
      // through to the text path so the button still does something
      // useful. The format reported to the beacon mirrors which payload
      // the OS share-sheet actually accepted, not which one the player
      // intended.
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: SITE_URL });
        markShareCopied("image");
        return;
      }
      await navigator.share({ text: `${text}\n\n${SITE_URL}`, url: SITE_URL });
      markShareCopied("text");
    } catch {
      /* User cancelled or the platform rejected; Copy buttons remain as
         the universal fallback, so silence is the right behavior. */
    }
  };

  return (
    <section class="sharegrid" aria-label="Share">
      <div class="sharegrid__image-wrap">
        {imageUrl != null && (
          <img
            class="sharegrid__image"
            src={imageUrl}
            alt="Your PalinDragon share grid"
          />
        )}
      </div>
      <div class="sharegrid__controls">
        <div class="sharegrid__actions" role="group" aria-label="Share">
          {canShare && (
            <button
              type="button"
              class="sharegrid__action"
              onClick={onNativeShare}
              aria-label="Share via system"
            >
              <ActionIcon d={SHARE_ICON_PATH} />
              <span>Share…</span>
            </button>
          )}
          <button
            type="button"
            class="sharegrid__action"
            onClick={onCopyImage}
            aria-label="Copy share grid as image"
          >
            <ActionIcon d={CLIPBOARD_ICON_PATH} />
            <span>{copied === "image" ? "Copied!" : "Image"}</span>
          </button>
          <button
            type="button"
            class="sharegrid__action"
            onClick={onCopyText}
            aria-label="Copy share grid as text"
          >
            <ActionIcon d={CLIPBOARD_ICON_PATH} />
            <span>{copied === "text" ? "Copied!" : "Text"}</span>
          </button>
        </div>
      </div>
    </section>
  );
}
